import { useState } from "react";
import useCartStore from "../../../../stores/useCartStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import db from "../../../../database";

const useAdditionalCharges = ({ pricePrecision }) => {
  const [additionalCharges, setAdditionalCharges] = useState(false);
  const [serviceChargeEnabled, setServiceChargeEnabled] = useState(false);
  const [serviceChargePercentage, setServiceChargePercentage] = useState(5);
  const [tipMode, setTipMode] = useState("Percentage");
  const [tipPercentage, setTipPercentage] = useState(0);
  const [tipAmount, setTipAmount] = useState(0);
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [packingCharge, setPackingCharge] = useState(0);
  const [roundingOption, setRoundingOption] = useState("No Rounding");

  const toFixedTruncate = (num, decimalPlaces = 3) => {
    if (isNaN(num) || num === null) num = 0;
    const factor = Math.pow(10, decimalPlaces);
    const truncatedNum = Math.trunc(num * factor) / factor;
    return truncatedNum.toFixed(decimalPlaces);
  };

  const applyRounding = (value, precision = pricePrecision) => {
    if (isNaN(value) || value === null) return 0;
    const factor = Math.pow(10, precision);
    switch (roundingOption) {
      case "Round Up":
        return Math.ceil(value * factor) / factor;
      case "Round Down":
        return Math.floor(value * factor) / factor;
      case "No Rounding":
      default:
        return parseFloat(toFixedTruncate(value, precision));
    }
  };

  const computeCharges = (subtotal) => {
    const serviceChargeAmount = applyRounding(serviceChargeEnabled ? (subtotal * serviceChargePercentage) / 100 : 0);
    const tipTotal = applyRounding(tipMode === "Percentage" && tipPercentage > 0 ? (subtotal * tipPercentage) / 100 : tipAmount);
    const deliveryChargeAmount = applyRounding(deliveryCharge);
    const packingChargeAmount = applyRounding(packingCharge);
    return { serviceChargeAmount, tipTotal, deliveryChargeAmount, packingChargeAmount };
  };

  const handleAdditionalCharges = () => {
    setAdditionalCharges(true);
  };

  const handleApplyCharges = async (setCart) => {
    setAdditionalCharges(false);
    const localCart = useCartStore.getState().cart;
    if (localCart) {
      const subtotal = localCart?.subTotal || 0;
      const { serviceChargeAmount, tipTotal, deliveryChargeAmount, packingChargeAmount } = computeCharges(subtotal);
      const chargeFields = {
        serviceCharge: serviceChargeAmount,
        tip: tipTotal,
        deliveryCharge: deliveryChargeAmount,
        deiveryCharge: deliveryChargeAmount,
        packingCharge: packingChargeAmount,
      };
      const updatedLocalCart = { ...localCart, ...chargeFields };
      setCart(updatedLocalCart);

      const tableName = useRestaurantStore.getState().tableName;
      if (tableName?.cwrFbTableId) {
        const fbOrders = await db.fbOrderData.where("cwrFbTableId").equals(tableName.cwrFbTableId).toArray();
        const activeOrder = fbOrders.find((o) => o.fbOrderStatus === "IP");
        if (activeOrder) {
          const updatedOrderCart = activeOrder.cart ? { ...activeOrder.cart, ...chargeFields } : { ...updatedLocalCart };
          await db.fbOrderData.update(activeOrder.id, { cart: updatedOrderCart });
        }
      }
    }
  };

  const resetToInitialState = (setCart) => {
    setServiceChargeEnabled(false);
    setServiceChargePercentage(5);
    setTipMode("Percentage");
    setTipPercentage(0);
    setTipAmount(0);
    setDeliveryCharge(0);
    setPackingCharge(0);
    setRoundingOption("No Rounding");
    const localCart = useCartStore.getState().cart;
    if (localCart) {
      const updatedLocalCart = {
        ...localCart,
        serviceCharge: 0,
        tip: 0,
        deliveryCharge: 0,
        deiveryCharge: 0,
        packingCharge: 0,
      };
      setCart(updatedLocalCart);
    }
  };

  return {
    additionalCharges,
    setAdditionalCharges,
    serviceChargeEnabled,
    setServiceChargeEnabled,
    serviceChargePercentage,
    setServiceChargePercentage,
    tipMode,
    setTipMode,
    tipPercentage,
    setTipPercentage,
    tipAmount,
    setTipAmount,
    deliveryCharge,
    setDeliveryCharge,
    packingCharge,
    setPackingCharge,
    roundingOption,
    setRoundingOption,
    applyRounding,
    computeCharges,
    handleAdditionalCharges,
    handleApplyCharges,
    resetToInitialState,
  };
};

export default useAdditionalCharges;
